package org.vreo.devicehub.features.settings.presentation.mainSettings

import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ErrorOutline
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.material3.adaptive.ExperimentalMaterial3AdaptiveApi
import androidx.compose.material3.adaptive.currentWindowAdaptiveInfo
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.window.core.layout.WindowSizeClass

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.device_control_unavailable_label

/**
 * Main settings (top level) screen
 * @param viewModel main settings viewmodel
 * @param liveViewContent content of the live access view
 * @param settingsSubView content of the device settings
 */
@Composable
fun MainSettingsRoot(
    viewModel: MainSettingsViewModel = koinViewModel(),
    liveViewContent: @Composable () -> Unit,
    settingsSubView: @Composable () -> Unit,
) {
    val state: MainSettingsState by viewModel.state.collectAsStateWithLifecycle()
    MainSettingsScreen(
        state = state,
        liveViewContent = liveViewContent,
        settingsContent = settingsSubView,
    )
}

/**
 * Main device settings view.
 *
 * On wide screens (medium/expanded width) the live view occupies 68 % of the horizontal
 * space and the settings panel 32 %, separated by a vertical divider.
 * On compact screens both panels are stacked vertically in a single scrollable column.
 *
 * @param state state of the view
 * @param liveViewContent live access view composable
 * @param settingsContent device settings composable
 * @param windowSizeClass window size class; defaults to the current adaptive window info
 * @param scrollState scroll state shared by the settings/narrow-layout column
 */
@OptIn(ExperimentalMaterial3AdaptiveApi::class)
@Composable
fun MainSettingsScreen(
    state: MainSettingsState,
    liveViewContent: @Composable () -> Unit,
    settingsContent: @Composable () -> Unit,
    windowSizeClass: WindowSizeClass = currentWindowAdaptiveInfo().windowSizeClass,
    scrollState: ScrollState = rememberScrollState(),
) {
    val isWideScreen = windowSizeClass.isWidthAtLeastBreakpoint(WindowSizeClass.WIDTH_DP_MEDIUM_LOWER_BOUND)

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        // Device unavailable warning banner — only shown when device is inactive
        if (!state.isDeviceActive) {
            Surface(
                color = MaterialTheme.colorScheme.errorContainer,
                shape = MaterialTheme.shapes.small,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Row(
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    Icon(
                        imageVector = Icons.Default.ErrorOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onErrorContainer,
                    )
                    Text(
                        text = stringResource(Res.string.device_control_unavailable_label),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onErrorContainer,
                    )
                }
            }
        }

        if (isWideScreen) {
            // Wide layout: live view (68%) | divider | settings panel (32%)
            Row(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth(),
            ) {
                Column(
                    modifier = Modifier
                        .weight(0.68f)
                        .fillMaxSize()
                        .verticalScroll(rememberScrollState()),
                ) {
                    liveViewContent()
                }

                VerticalDivider()

                Column(
                    modifier = Modifier
                        .weight(0.32f)
                        .fillMaxSize()
                        .verticalScroll(scrollState)
                        .padding(20.dp),
                ) {
                    settingsContent()
                }
            }
        } else {
            // Narrow layout: live view stacked above settings, single scroll
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .verticalScroll(scrollState),
            ) {
                liveViewContent()

                HorizontalDivider(modifier = Modifier.padding(vertical = 12.dp))

                settingsContent()
            }
        }
    }
}

@Preview
@Composable
fun MainSettingsPreview() {
    MaterialTheme {
        MainSettingsScreen(
            state = MainSettingsState(isDeviceActive = true),
            liveViewContent = {
                Text("Live Stream")
            },
            settingsContent = {
                Text("Settings")
            },
        )
    }
}
